import { Fragment, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Button, Table, UncontrolledTooltip } from 'reactstrap'
import { FaPlus, FaCaretDown, FaCaretUp } from 'react-icons/fa'
import { gql } from '@apollo/client'
import { getTenant, getAlignment, hasActionType, getActionOfType, makeInteger, getHookInfo, getBaseName, replaceUnicode, getAttributeValue } from '../../utils'
import allHooks from '../../hooks/allHooks'
import { SET_USER_ATTRIBUTE_MUTATION } from '../../graphql'
import { FIELD_TYPE, ACTION_TYPE, ATTRIBUTE_NAME } from '../../constants'
import FileLink from '../FileLink/FileLink'
import CSVLink from '../CSVLink/CSVLink'
import PDFLink from '../PDFLink/PDFLink'
import PaginationPageControls from '../Pagination/PaginationPageControls'
import PaginationRecordMenu from '../Pagination/PaginationRecordMenu'
import './SearchResults.css'

export const USER_ATTRIBUTE_MUTATION = gql(SET_USER_ATTRIBUTE_MUTATION)

export default function SearchResults(props) {
  const { searchResults, inputs, lastColumnSorted, sortDir, addButton, viewLink, hasSubtotal } = props
  console.log('SearchResults rendered: ', props)

  const navigate = useNavigate()
  const location = useLocation()
  const showObjectId = getAttributeValue(ATTRIBUTE_NAME.showObjectId)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const scrollTop = params.get('scrollTop')
    if (scrollTop) {
      divRef.current.scrollTo(0, scrollTop)
    }
  }, [location.search])

  function getCellComponent(cellData, fieldTypeId) {
    let cellComponent = cellData
    if (cellData) {
      if (fieldTypeId === FIELD_TYPE.file) {
        cellComponent = <FileLink fileName={cellData} jwt={searchResults.fileJwt} />
      }
      if (fieldTypeId === FIELD_TYPE.currency) {
        cellComponent = cellData
      }
    }
    return cellComponent
  }

  function encodeSource() {
    let currentPath = `${location.pathname}${location.search}`
    if (currentPath.includes('scrollTop')) {
      currentPath = currentPath.substr(0, currentPath.indexOf('scrollTop')-1)
    }
    const delimiter = currentPath.includes('?') ? '&' : '?'
    const scrollTop = divRef.current.scrollTop ? `${delimiter}scrollTop=${divRef.current.scrollTop}` : ''
    const source = encodeURIComponent(`${currentPath}${scrollTop}`)
    console.log('SearchResults.encodeSource: ', source)
    return source
  }

  function modifyButtonClicked(objectId, modifyAction) {
    console.log('SearchResults.modifyButtonClicked: ', objectId, modifyAction)
    const source = encodeSource()
    const url = `/object/${objectId}?actionId=${modifyAction.id}&source=${source}`
    navigate(url)
  }
  
  function viewButtonClicked(objectId) {
    console.log('SearchResults.viewButtonClicked: ', objectId)
    const source = encodeSource()
    const url = (`/object/${objectId}?source=${source}`)
    navigate(url)
  }

  // create button
  let createButton = null
  if (hasActionType(searchResults.userActions, ACTION_TYPE.submit) && addButton !== 'N') {
    const objectTypeName = searchResults.search.objectType.name
    const path = `/create/${searchResults.search.objectType.id}?source=${location.pathname}`
    createButton =
      <Link to={path}>
        <Button data-cy='search-results-create-button' className="search-results-create-button btn-sm" color="success">
          <FaPlus className="create-icon" /> {objectTypeName}
        </Button>
      </Link>
  }

  // modify button
  let showModifyButton = searchResults.search.objectType.stateful === 'Y' ? false : true
  let hasModifyAction = false
  let modifyAction = null
  if (showModifyButton) {
    hasModifyAction = hasActionType(searchResults.userActions, ACTION_TYPE.modify)
    if (hasModifyAction) {
      modifyAction = getActionOfType(searchResults.userActions, ACTION_TYPE.modify)
    } else {
      showModifyButton = false
    }
  }

  // sort column
  function sortColumnClicked(e) {
    let columnIndex
    if (!e.target.id) {
      columnIndex = e.target.farthestViewportElement.id
    } else {
      columnIndex = e.target.id
    }
    console.log('SearchResults.sortColumnClicked: ', {lastColumnSorted, columnIndex, sortDir, inputs})

    const inputsString = inputs ? `&inputs=${JSON.stringify(inputs)}` : ''
    let newSortDir = 'ASC'
    if ((lastColumnSorted === null && columnIndex === '1') || (lastColumnSorted === columnIndex && sortDir === 'ASC')) {
      newSortDir = 'DESC'
    }
    navigate(`/searchResults/${searchResults.search.id}?start=${searchResults.start}${inputsString}&sortColumn=${columnIndex}&sortDir=${newSortDir}`)
  }

  // search result table headings
  const columns = []
  let columnIndex = 1
  if (!hasSubtotal) {
    columns.push(<th key={-1}>#</th>)
  }
  if (viewLink !== 'N' && !hasSubtotal) {
    columns.push(<th key={0}>{createButton}</th>)
  }
  for (let column of searchResults.details) {
    let columnName = ''
    if (column.field && column.showColumnHeading === 'Y') {
      columnName = column.field.name
    }
    if (lastColumnSorted === null || makeInteger(lastColumnSorted) !== makeInteger(columnIndex)) {
      columns.push(<th className='search-results-column' data-cy='search-results-column' key={columnIndex} id={columnIndex} onClick={(e) => sortColumnClicked(e)}> {columnName} </th>)
    } else if (makeInteger(lastColumnSorted) === makeInteger(columnIndex)) {
      if (sortDir === 'ASC') {
        columns.push(<th className='search-results-column' data-cy='search-results-column' key={columnIndex} id={columnIndex} onClick={(e) => sortColumnClicked(e)}> {columnName} <FaCaretUp id={columnIndex}/> </th>)
      } else {
        columns.push(<th className='search-results-column' data-cy='search-results-column' key={columnIndex} id={columnIndex} onClick={(e) => sortColumnClicked(e)}> {columnName} <FaCaretDown id={columnIndex}/> </th>)
      }
    }
    columnIndex++
  }

  // for each row in the table
  const rows = []
  for (let dataRowIndex = 0; dataRowIndex < searchResults.results.length; dataRowIndex++) {
    let dataRow = searchResults.results[dataRowIndex]
    let rowObjectId = dataRow[0] 
    const row = []
    // for each cell in a row
    for (let dataCellIndex = 0; dataCellIndex < dataRow.length; dataCellIndex++) {
      let dataCell = dataRow[dataCellIndex]
      if (dataCellIndex === 0) {
        // result line number
        if (!hasSubtotal) {
          if (dataRowIndex === 0) {
            row.push(<td className="right" data-cy="record-found" key={-1}>{dataRowIndex + searchResults.start}</td>)
          } else {
            row.push(<td className="right" key={-1}>{dataRowIndex + searchResults.start}</td>)
          }
          let viewButton = null
          let modifyButton = null
          if (viewLink !== 'N') {
            // "View" button
            let dataButtonName = ''
            if (dataRow[1]) {
              dataButtonName = dataRow[1].toLowerCase().replace(/ /g,'-')
            }
            viewButton = 
              <Button 
                onClick={() => viewButtonClicked(dataCell)}
                data-name={`search-results-view-button-${dataButtonName}`}
                data-cy="search-results-view-button"
                className={`${dataCell} search-results-view-button`}
                color="primary"
                size="sm">View</Button>
          }
          if (showModifyButton) {
            // "Modify" button
            modifyButton = 
              <Button
                onClick={() => modifyButtonClicked(dataCell, modifyAction)}
                color="warning"
                size="sm"
                className="search-results-modify-button">Edit</Button>
          }
          row.push(<td key={0} style={{ whiteSpace: 'nowrap'}}>{viewButton} {modifyButton}</td>)
        }
      } else {
        let cellClassName
        let cellComponent
        //console.log(dataCellIndex, searchResults.details[dataCellIndex - 1])
        if (searchResults.details[dataCellIndex - 1].outputHook) {
          const hookInfo = getHookInfo(searchResults.details[dataCellIndex - 1].outputHook)
          cellClassName = 'centered'
          cellComponent = allHooks[hookInfo.moduleName][hookInfo.functionName](dataRow[0], searchResults.details[dataCellIndex - 1].id, dataCell, location)
        } else {
          cellClassName = getAlignment(parseInt(searchResults.details[dataCellIndex - 1].field.type.id))
          cellComponent = getCellComponent(dataCell, parseInt(searchResults.details[dataCellIndex - 1].field.type.id))
        }
        const htmlId = searchResults.details[dataCellIndex - 1].field ? parseInt(searchResults.details[dataCellIndex - 1].field.id) : ''
        row.push(<td className={cellClassName} key={dataCellIndex} id={htmlId}>{cellComponent}</td>)
      }
    }
    let style = {}
    if (hasSubtotal && dataRowIndex === searchResults.results.length - 1) {
      style = { fontWeight: 'bold' }
    }
    const tooltip = showObjectId === 'Y' ? <UncontrolledTooltip autohide={false} placement="left" target={`ID${rowObjectId}`}>{`ID: ${rowObjectId}`}</UncontrolledTooltip> : ''
    rows.push(
      <>
        <tr id={`ID${rowObjectId}`} style={style} key={dataRowIndex} data-cy="search-results-row" data-object-id={rowObjectId}>
          {row}
        </tr>
        {tooltip}
      </>
    )
  }

  // pdf and csv link
  const tenant = getTenant()
  const csvUrl = `${process.env.REACT_APP_SERVER}/csv/${tenant}/${encodeURIComponent(searchResults.search.name)}.csv?searchId=${searchResults.search.id}&inputs=${JSON.stringify(inputs)}`
  const pdfUrl = `${getBaseName()}pdf/${encodeURIComponent(searchResults.search.name)}.pdf?searchId=${searchResults.search.id}&inputs=${JSON.stringify(inputs)}`

  let searchResultsheader = ''
  let recordCount = ''
  let pageControls = ''
  if (hasSubtotal) {
    searchResultsheader = 
      <div style={{ marginTop: '10px' }} className="search-results-header">
        <div>
          <CSVLink url={csvUrl} />
        </div>
        <h3 style={{ fontSize: '1.2rem' }}>{replaceUnicode(searchResults.search.name)}</h3>
      </div>
  } else {
    pageControls = <PaginationPageControls results={searchResults} />
    recordCount = <PaginationRecordMenu results={searchResults} inputs={inputs} location={location} />
    searchResultsheader =
      <Fragment>
        <div style={{ marginTop: '5px' }} className="search-results-header">
          <div style={{ display: 'inline-block' }}>
            < PDFLink url={pdfUrl} />
          </div>
          <h3 style={{ display: 'inline-block', margin: '0px 20px', verticalAlign: 'bottom', fontSize: '1.2rem' }}>{replaceUnicode(searchResults.search.name)}</h3>
          <div style={{ display: 'inline-block' }}>
            <CSVLink url={csvUrl} />
          </div>
        </div>
        {recordCount}
        {pageControls}
      </Fragment>
  }

  const divRef = useRef()
  return (
    <div className="search-results-scroll-container" ref={divRef}>
      <div className="search-results-container">
        { searchResultsheader}
        <div className="search-results-table-width-container">
          <div className="search-results-table-container">
            <Table className="search-results-table" data-cy="search-results-table" bordered hover size="sm">
              <thead>
                <tr>
                  {columns}
                </tr>
              </thead>
              <tbody>
                {rows}
              </tbody>
            </Table>
          </div>
        </div>
        {pageControls}
        <div data-cy-search-results-complete="yes"></div>
      </div >
    </div>

  )
}